// src/lib/statusColors.ts
// Single source of truth for status pill/dot colouring across the app.
// Every status pill — dashboard queue badges, the ActionBar state pill,
// StatusBadge (workflow + stock), audit log rows/detail — resolves its
// dot and pill colours from this map so they can never drift out of sync
// with each other again.
import { brand } from '@/lib/tokens';

export type StatusTone = 'grey' | 'orange' | 'green' | 'red' | 'accent';

export const TONE_COLORS: Record<StatusTone, { color: string; background: string; border: string }> = {
  grey: {
    color:      'rgba(var(--fg-rgb),0.6)',
    background: 'rgba(var(--fg-rgb),0.06)',
    border:     'rgba(var(--fg-rgb),0.16)',
  },
  orange: {
    color:      brand.warning,
    background: brand.warningTint,
    border:     'rgba(251,146,60,0.3)',
  },
  green: {
    color:      brand.success,
    background: brand.successTint,
    border:     'rgba(74,222,128,0.3)',
  },
  red: {
    color:      brand.error,
    background: brand.errorTint,
    border:     'rgba(255,180,171,0.3)',
  },
  accent: {
    color:      brand.accent,
    background: brand.accentTint,
    border:     'rgba(104,187,203,0.3)',
  },
};

/**
 * Canonical tone for every status string used anywhere in the app.
 * Grouping:
 *  - grey   -> neutral / system-in-progress (no operator action needed yet)
 *  - orange -> waiting on a person (operator review, substitution decision)
 *  - green  -> success / completed
 *  - red    -> blocked / negative / failed
 *  - accent -> the operator's own in-progress editing step (not a queue state)
 */
export const STATUS_COLORS: Record<string, StatusTone> = {
  // Backend workflow statuses (raw)
  RECEIVED: 'grey',
  PROCESSING: 'grey',
  DRAFTED: 'grey',
  PENDING_APPROVAL: 'orange',
  PENDING_REVIEW: 'orange',
  MANUAL_REVIEW_REQUIRED: 'orange',
  APPROVED: 'green',
  RESERVATION_CONFIRMED: 'grey',
  SENT: 'green',
  REJECTED: 'red',
  FAILED: 'red',
  QUARANTINED: 'red',

  // Dashboard inbox badge labels (display strings from getInboxBadge)
  INBOUND: 'grey',
  'DRAFTING REPLY': 'grey',
  'PENDING REVIEW': 'orange',
  'CONFIRMING RESERVATION': 'grey',

  // Frontend workflow-state pill (ActionBar) — the operator's current step
  STOCK_RESERVATION: 'accent',
  EDIT_MODE: 'accent',
  FINALIZE: 'accent',

  // Stock / reservation line-item statuses
  IN_STOCK: 'green',
  LOW_STOCK: 'orange',
  SUBSTITUTION: 'orange',
  SUBSTITUTED: 'orange',
  UNAVAILABLE: 'red',
  HELD: 'accent',
  CONFLICT: 'red',

  // Audit log actions
  LOGIN: 'grey',
  EDITED: 'accent',
  DELETED: 'red',
  RESTORED: 'green',
  REPLY_SENT: 'green',
  // 14 Sep 2026: posted by workflow-service's _send_approval_quote_email
  // when the background send/transition fails after finalize-send — see
  // [[finding-injozi-approved-stall-swallowed-exception]]. Without this
  // entry the row would still render safely (falls through to grey via
  // toneFor's ?? 'grey' default) but a real failure reading as neutral
  // grey defeats the point of surfacing it at all.
  SEND_FAILED: 'red',

  // Replies tab detail panel badge (not a workflow status — a client
  // thread-reply blocked from re-entering the pipeline, see
  // [[finding-reply-after-send-not-blocked]])
  REPLY: 'accent',

  // Hold status (SENT bookings — Part 1 hold_expires_at/hold_expired)
  HOLD_ACTIVE: 'grey',
  HOLD_EXPIRED: 'red',
};

export function toneFor(status: string | undefined | null): StatusTone {
  if (!status) return 'grey';
  return STATUS_COLORS[status] ?? 'grey';
}

export function statusColors(status: string | undefined | null) {
  return TONE_COLORS[toneFor(status)];
}
